export const CATEGORY_OPTIONS = [
  { label: 'Airfare', value: 'airfare' },
  { label: 'Lodging', value: 'lodging' },
  { label: 'Meals', value: 'meals' },
  { label: 'Ground Transportation', value: 'ground_transportation' },
  { label: 'Mileage', value: 'mileage' },
  { label: 'Office Supplies', value: 'office_supplies' },
  { label: 'Technology Reimbursement', value: 'technology_reimbursement' },
  { label: 'Pre-Approved Meal Reimbursement for All Hands Meeting', value: 'all_hands_meal' },
  { label: 'Other', value: 'other' },
];

export const DEFAULT_CATEGORY = 'other';
export const TECHNOLOGY_REIMBURSEMENT_CATEGORY = 'technology_reimbursement';

// Pre-approved: the employee only picks the meeting date. The server fixes the
// amount (served by GET /expenses/meta/all-hands-meal) and files it straight
// into Approved with no receipt and no reviewer, so it has no approver.
export const ALL_HANDS_MEAL_CATEGORY = 'all_hands_meal';

// Paid without a receipt, so an expense in one is never flagged for lacking one.
export const RECEIPT_FREE_CATEGORIES = new Set([TECHNOLOGY_REIMBURSEMENT_CATEGORY, ALL_HANDS_MEAL_CATEGORY]);

export const isAllHandsMealCategory = (category) =>
  String(category || '').trim().toLowerCase() === ALL_HANDS_MEAL_CATEGORY;

export const ALLOWED_CATEGORIES = new Set(CATEGORY_OPTIONS.map((item) => item.value));

// Vendor name is only meaningful (and required) for these categories. Every
// other category leaves it optional. Keep this in sync with the server's copy
// in server/routes/expenses.js.
export const VENDOR_REQUIRED_CATEGORIES = new Set([
  'airfare',
  'ground_transportation',
  'lodging',
  'other',
]);

export const isVendorNameRequired = (category) =>
  VENDOR_REQUIRED_CATEGORIES.has(normalizeCategory(category));

// Categories where a "Travel?" question makes sense. Office supplies and
// technology reimbursements are never travel-related, so the question is hidden.
export const TRAVEL_ELIGIBLE_CATEGORIES = new Set([
  'airfare',
  'lodging',
  'meals',
  'ground_transportation',
  'mileage',
  'other',
]);

export const isTravelEligibleCategory = (category) =>
  TRAVEL_ELIGIBLE_CATEGORIES.has(normalizeCategory(category));

// Categories that are inherently travel expenses. For these the "Travel?"
// toggle is forced on and locked, since the purchase can't be anything else.
export const TRAVEL_REQUIRED_CATEGORIES = new Set([
  'airfare',
  'lodging',
  'meals',
  'ground_transportation',
]);

export const isTravelRequiredCategory = (category) =>
  TRAVEL_REQUIRED_CATEGORIES.has(normalizeCategory(category));

export const LEGACY_CATEGORY_MAP = {
  travel: 'airfare',
  software: 'other',
  training: 'other',
  client_entertainment: 'meals',
};

const CATEGORY_LABEL_BY_VALUE = new Map(CATEGORY_OPTIONS.map((item) => [item.value, item.label]));

export const normalizeCategory = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  const mapped = LEGACY_CATEGORY_MAP[normalized] || normalized;
  return ALLOWED_CATEGORIES.has(mapped) ? mapped : DEFAULT_CATEGORY;
};

// Like normalizeCategory, but keeps "nothing chosen yet" as an empty string
// instead of falling back to Other. The new expense form starts with no
// category and only reveals the rest of its fields once one is picked, so it
// needs to tell "unanswered" apart from "answered: Other".
export const normalizeCategoryOrEmpty = (value) => {
  const raw = String(value || '').trim();
  return raw ? normalizeCategory(raw) : '';
};

// Returns the human-friendly label for a stored category value. Unknown values
// fall back to a title-cased version of the raw value rather than the system slug.
export const getCategoryLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const known = CATEGORY_LABEL_BY_VALUE.get(raw) || CATEGORY_LABEL_BY_VALUE.get(normalizeCategory(raw));
  if (known) return known;

  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
